<template>
  <v-container fluid class="pa-0 flex-grow-1 d-flex flex-column">
    <v-row class="header ma-0">
      <v-col cols="2" class="pa-3 font-weight-bold d-block">
        <span class="pa-3 font-weight-bold d-block">
          {{ tkey('clusteringSchemes') }}
        </span>
      </v-col>
      <v-col cols="8" class="pa-3 font-weight-bold d-block">
        <span class="pa-3 font-weight-bold d-block">
          {{ selectedClusterSchemeName }}
        </span>
      </v-col>
      <v-col cols="2">
        <v-select
          id="refSelectClusterName"
          v-model="selectedClusterToMoveTo"
          :disabled="!hasSelectedStores"
          :items="selectableClusterNames"
          :placeholder="tkey('moveSelectedStoresToCluster')"
          @update:model-value="moveStores($event)"
        />
      </v-col>
    </v-row>
    <v-divider />
    <v-row class="main-panel flex-grow-1">
      <v-col cols="2" class="rtls-border rtls-border--right">
        <v-container
          id="ref-cluster-schemes-table"
          :class="{ 'empty-table': !getClusterSchemes.length }"
        >
          <!-- Headers -->
          <v-row id="headers-row">
            <v-col
              v-for="(header, index) in headers"
              :key="`header-${index}`"
              :cols="header.cols"
              :class="header.headerClass"
            >
              <span>{{ header.text }}</span>
            </v-col>
          </v-row>
          <v-row
            v-for="row in clusterSchemes"
            :key="row._id"
            class="cluster-row"
            :class="{
              selected: selectedScheme && selectedScheme._id === row._id,
            }"
          >
            <v-col
              v-for="header in headers"
              :key="`cell-${row._id}-${header.id}`"
              style="height: 30px"
              :cols="header.cols"
              :class="header.colClass"
            >
              <template v-if="header.ui === CELL_TYPE.icon">
                <v-icon
                  v-if="showClusterWarning(header.id, row)"
                  :class="header.iconClass"
                  :disabled="isNavigationDisabled || row.disabled"
                  style="height: 100%"
                  @click="header.onClick(row)"
                >
                  {{ header.value }}
                </v-icon>
              </template>
              <template v-else>
                <span class="d-flex align-center pr-3" style="height: 100%">
                  {{ row[header.value] }}
                </span>
              </template>
            </v-col>
          </v-row>
        </v-container>
        <v-divider horizontal />
        <v-btn class="mt-2 ml-2" primary @click="openClusterCreationModal">
          {{ tkey('addScheme') }}
        </v-btn>
      </v-col>

      <v-col cols="10">
        <div style="width: 100%; height: 100%">
          <v-divider />
          <StoreAllocationTable
            v-if="selectedScheme"
            :key="`ref-store-allocation-table-${selectedScheme._id}`"
            ref="ref-store-allocation-table"
            mode="reference-cluster"
            @has-selected-rows="hasSelectedStores = $event"
            @data-changed="hasDataChanges = $event"
            @on-save="onSave()"
          />
        </div>
      </v-col>
    </v-row>

    <UnsavedDataModal
      ref="unsavedDataModal"
      :value="isUnsavedDataModalOpen"
      @cancel="closeUnsavedDataModal"
      @confirm="closeUnsavedDataModal"
    />

    <ClusterCreationModal
      v-model="isClusterCreationModalOpen"
      @process="processNewClusterSchemes"
      @close="closeClusterCreationModal"
    />

    <!-- edit confirmation dialog -->
    <v-dialog v-model="isEditConfirmationModalOpen" :max-width="300">
      <BaseDialogCard class="pb-0" @close="toggleIsEditConfirmationModalOpen(false)">
        <template #header>
          <v-card-text class="pa-4 pb-0">
            <p class="mb-4">{{ tkey('edit.question') }}</p>
            <v-text-field
              v-model="editClusterSchemeName"
              hide-details
              density="compact"
              variant="underlined"
            />
          </v-card-text>
        </template>
        <template #footer>
          <v-card-actions class="d-flex align-center pa-4 pt-0">
            <div class="d-flex align-center">
              <v-tooltip v-if="nameHasError" location="top">
                <template #activator="{ props }">
                  <i v-bind="props" class="material-icons-round error-triangle-icon ml-5">
                    warning_amber
                  </i>
                </template>
                <span>{{ $t('validationErrors.unique', [tkey('clusterSchemeName')]) }}</span>
              </v-tooltip>
              <v-btn
                color="primary"
                variant="flat"
                max-height="28"
                :disabled="nameHasError"
                @click="confirmEdit(editClusterSchemeId)"
              >
                {{ tkey('edit.action') }}
              </v-btn>
            </div>
            <v-btn variant="text" class="cancel" @click="toggleIsEditConfirmationModalOpen(false)">
              {{ $t('actions.cancel') }}
            </v-btn>
          </v-card-actions>
        </template>
      </BaseDialogCard>
    </v-dialog>

    <!-- delete confirmation dialog -->
    <v-dialog v-model="isDeleteConfirmationModalOpen" :max-width="450" content-class="rtls-dialog">
      <BaseDialogCard
        :title="$t('actions.confirm')"
        :border="true"
        @close="toggleIsDeleteConfirmationModalOpen(false)"
      >
        <v-card-text class="text-h6">
          <p>{{ tkey('delete.question') }}</p>
          <strong>{{ tkey('delete.warning') }}</strong>
        </v-card-text>
        <template #footer>
          <v-card-actions>
            <v-btn primary @click="toggleIsDeleteConfirmationModalOpen(false)">
              {{ $t('actions.cancel') }}
            </v-btn>
            <v-btn
              secondary
              variant="flat"
              class="outlined-btn"
              @click="confirmDelete(editClusterSchemeId)"
            >
              {{ tkey('delete.action') }}
            </v-btn>
          </v-card-actions>
        </template>
      </BaseDialogCard>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions, mapState } from 'pinia';
import TableEnum from '@enums/table/index';
import { filter, get, isEqual, some, trim } from 'lodash';
import { useContextStore } from '@/stores/context';
import { useReferenceClusteringStore } from '@/stores/reference-clustering';
import { useToolDataStore } from '@/stores/tool-data';
import { useSnackbarStore } from '@/stores/snackbar';
import { useUnsavedDataWarning } from '@/composables/unsaved-data-warning';
import { useInternationalization } from '@/composables/use-internationalization';

export default {
  beforeRouteLeave(to, from, next) {
    this.beforeRouteLeave(to, from, next);
  },
  setup() {
    const {
      beforeNavWithUnsavedData,
      beforeRouteLeave,
      closeUnsavedDataModal,
      isUnsavedDataModalOpen,
    } = useUnsavedDataWarning();
    const { tkey } = useInternationalization('clientAdminPage');
    return {
      beforeNavWithUnsavedData,
      beforeRouteLeave,
      tkey,
      closeUnsavedDataModal,
      isUnsavedDataModalOpen,
    };
  },
  data() {
    return {
      CELL_TYPE: TableEnum.CELL_TYPE,
      clusterSchemes: [],
      selectedClusterSchemeName: '',
      editClusterSchemeName: '',
      editClusterSchemeId: null, // TODO this could be simplified and use a single object
      selectedClusterToMoveTo: null, // used for store-allocation-list edit
      hasDataChanges: false,
      hasSelectedStores: false, // used to disable cluster select when no stores selected
      isClusterCreationModalOpen: false,
      headers: [
        {
          value: 'name',
          ui: TableEnum.CELL_TYPE.readonly,
          id: 'schemeName',
          cols: 5,
          headerClass: 'pl-2 px-0 py-0 pr-0',
          colClass: 'pl-2 px-0 py-0 pr-0',
          onClick: this.handleExpandClusteringScheme,
          text: this.tkey('clusterSchemeName'),
        },
        {
          text: this.tkey('clusterCount'),
          value: 'clusterCount',
          class: 'pl-0',
          cols: 2,
          headerClass: 'pa-0 header-text',
          colClass: 'pa-0 justify-end',
          ui: TableEnum.CELL_TYPE.readonly,
          id: 'clusterCount',
        },
        {
          class: 'pa-0',
          cols: 1,
          ui: TableEnum.CELL_TYPE.icon,
          colClass: 'justify-end pr-1 pl-0 px-0 py-0',
          iconClass: 'v-icon-blue',
          onClick: this.handleDeleteClusteringScheme,
          value: '$trash',
          id: 'deleteButton',
        },
        {
          class: 'pa-0',
          cols: 1,
          ui: TableEnum.CELL_TYPE.icon,
          colClass: 'justify-end pr-1 pl-0 px-0 py-0',
          iconClass: 'v-icon-blue',
          onClick: this.handleEditClusteringScheme,
          value: '$edit',
          id: 'editButton',
        },
        {
          class: 'pa-0',
          cols: 1,
          ui: TableEnum.CELL_TYPE.icon,
          colClass: 'justify-end pr-1 pl-0 px-0 py-0',
          iconClass: 'v-icon-red',
          // onClick: this.handleEditClusteringScheme,
          value: '$warning',
          id: 'warningButton',
        },
        {
          class: 'pa-0',
          cols: 2,
          ui: TableEnum.CELL_TYPE.icon,
          colClass: 'justify-end pr-1 pl-0 px-0 py-0',
          iconClass: 'v-icon-blue',
          onClick: this.handleExpandClusteringScheme,
          value: 'mdi-chevron-right',
          id: 'expandButton',
        },
      ],
      isEditConfirmationModalOpen: false,
      isDeleteConfirmationModalOpen: false,
    };
  },

  computed: {
    ...mapState(useContextStore, ['getClientConfig']),
    ...mapState(useReferenceClusteringStore, [
      'getClusterSchemes',
      'selectedScheme',
      'selectableClusterNames',
    ]),
    ...mapState(useToolDataStore, ['stores']),

    hasDefaultClustersEnabled() {
      return get(this.getClientConfig, 'features.defaultClustersEnabled', false);
    },

    isNavigationDisabled() {
      return !this.getClusterSchemes.length;
    },

    invalidClusteringSchemes() {
      const isNoDataCluster = filter(this.getClusterSchemes, clScheme => {
        // cluster is invalid if it's marked as no data cluster
        return some(clScheme.clusters, 'isNoDataCluster');
      });

      const unassigned = filter(this.getClusterSchemes, clScheme => {
        // cluster is invalid if it's marked as no data cluster
        return some(clScheme.clusters, { clusterName: 'Unassigned' });
      });

      return [...isNoDataCluster, ...unassigned];
    },

    invalidClusteringScheme() {
      const isNoDataCluster = filter(this.getClusterSchemes, clScheme => {
        // cluster is invalid if it's marked as no data cluster
        return some(clScheme.clusters, 'isNoDataCluster');
      });

      const unassigned = filter(this.getClusterSchemes, clScheme => {
        // cluster is invalid if it's marked as no data cluster
        return some(clScheme.clusters, { clusterName: 'Unassigned' });
      });

      return [...isNoDataCluster, ...unassigned];
    },

    nameHasError() {
      return (
        this.editClusterSchemeName === '' ||
        !this.hasUniqueName({ name: this.editClusterSchemeName })
      );
    },

    showWarning() {
      return false;
    },
  },

  watch: {
    selectedScheme() {
      if (!this.selectedScheme) return;
      this.selectedClusterSchemeName = this.selectedScheme.name;
      this.setSelectableClusterNames(this.selectedScheme.clusters.map(c => c.clusterName));
    },
  },

  async created() {
    await this.reset();
  },

  methods: {
    ...mapActions(useSnackbarStore, ['showSuccess', 'showError']),
    ...mapActions(useToolDataStore, ['getStores']),
    ...mapActions(useReferenceClusteringStore, [
      'deleteClusterScheme',
      'fetchClusterScheme',
      'fetchClusterSchemes',
      'updateClusterSchemeName',
      'saveClusteringSchemes',
      'setSelectedScheme',
      'setSelectableClusterNames',
    ]),

    async reset() {
      await this.getStores();
      this.storeKeys = new Set(this.stores.map(c => c.storeKey));
      this.clusterSchemes = await this.fetchClusterSchemes();
      // set the first one as the selected scheme
      if (this.clusterSchemes) {
        this.setSelectedScheme(this.clusterSchemes[0]);
      }
    },

    showClusterWarning(headerId, scheme) {
      if (headerId !== 'warningButton') {
        return true;
      }
      if (!scheme.clusters || !scheme.clusterCount || scheme.clusterCount < 2) {
        return true;
      }
      if (some(scheme.clusters, 'isNoDataCluster')) {
        return true;
      }
      // get the stores assigned, if not the same as all stores, then alert
      const assignedStoreKeys = [];
      scheme.clusters.forEach(c => {
        assignedStoreKeys.push(...c.storeKeys);
      });
      const currentStoreKeys = new Set(assignedStoreKeys);
      if (!isEqual(currentStoreKeys, this.storeKeys)) {
        return true;
      }
      return false;
    },

    expandClusteringScheme(clusterScheme) {
      // if (clusterScheme.disabled) return;
      this.setSelectedScheme(clusterScheme);
      this.fetchClusterScheme(clusterScheme); // reload from the database
    },

    handleExpandClusteringScheme(clusterScheme) {
      this.beforeNavWithUnsavedData(() => {
        this.expandClusteringScheme(clusterScheme);
      });
    },

    async handleDeleteClusteringScheme(clusterScheme) {
      this.toggleIsDeleteConfirmationModalOpen(true);
      this.editClusterSchemeId = clusterScheme._id;
    },

    async confirmDelete(clusterSchemeId) {
      await this.deleteClusterScheme(clusterSchemeId);
      this.clusterSchemes = await this.fetchClusterSchemes();
      // set the first one as the selected scheme
      if (this.clusterSchemes) {
        this.setSelectedScheme(this.clusterSchemes[0]);
      }
      this.toggleIsDeleteConfirmationModalOpen(false);
    },

    async handleEditClusteringScheme(clusterScheme) {
      this.editClusterSchemeName = clusterScheme.name;
      this.editClusterSchemeId = clusterScheme._id;
      this.toggleIsEditConfirmationModalOpen(true);
    },

    async confirmEdit(clusterSchemeId) {
      await this.updateClusterSchemeName({ clusterSchemeId, name: this.editClusterSchemeName });
      this.clusterSchemes = await this.fetchClusterSchemes();
      // set the first one as the selected scheme
      if (this.clusterSchemes) {
        this.setSelectedScheme(this.clusterSchemes[0]);
      }
      this.toggleIsEditConfirmationModalOpen(false);
    },

    moveStores(groupClusterName) {
      this.$refs['ref-store-allocation-table'].moveRowsToGroup({ groupClusterName });
      // without $nextTick first reset will work but all subsequent ones won't work.
      // The vuetify internal lazy value gets out of sync with the actual value.
      // similar issue on promo page-navigator.vue cancelUpdatePageCount.
      this.$nextTick(() => {
        this.selectedClusterToMoveTo = null;
      });
    },

    openClusterCreationModal() {
      this.isClusterCreationModalOpen = true;
    },

    closeClusterCreationModal() {
      this.isClusterCreationModalOpen = false;
    },

    async processNewClusterSchemes(body) {
      await this.saveClusteringSchemes(body);
      this.clusterSchemes = await this.fetchClusterSchemes();
      // set the first one as the selected scheme
      if (this.clusterSchemes) {
        this.setSelectedScheme(this.clusterSchemes[0]);
      }
    },

    hasUniqueName({ name }) {
      const newName = name;
      // We check if the name is unique whithin the list to be created and the list of existing clusters
      const isUnique =
        filter(this.clusterSchemes, ({ name: existingName }) => {
          return isEqual(trim(String(newName)), trim(existingName));
        }).length === 1;

      return isUnique ? '' : this.$t('validationErrors.unique', [this.tkey('name')]);
    },

    async onSave() {
      // updating of clusters is done on the refresh for reference clusters
      await this.$refs['ref-store-allocation-table'].onSaveRefresh();
    },

    toggleIsEditConfirmationModalOpen(isOpen) {
      this.isEditConfirmationModalOpen = isOpen;
    },

    toggleIsDeleteConfirmationModalOpen(isOpen) {
      this.isDeleteConfirmationModalOpen = isOpen;
    },
  },
};
</script>

<style lang="scss">
@import '@style/base/_variables.scss';

$shadow-colour1: rgba(0, 0, 0, 0.2);
$shadow-colour2: rgba(0, 0, 0, 0.14);
$shadow-colour3: rgba(0, 0, 0, 0.12);

.empty-table {
  color: $assortment-disabled-action-green-button-colour;
}

.v-icon-blue {
  color: $assortment-action-icon-color !important;
  fill: $assortment-action-icon-color !important; // for SVG
}

.v-icon-red {
  color: $assortment-negative-action-colour !important;
  fill: $assortment-negative-action-colour !important; // for SVG
}

#ref-cluster-schemes-table {
  padding: 0;

  .cluster-row {
    &:nth-last-of-type(odd) {
      background-color: $assortment-table-blue-bg-colour;
    }

    .col {
      display: flex;
      align-items: center;
    }
    &.selected {
      position: relative;
      /* override existing border with this new one */
      border-left: 2px solid $assortment-action-icon-color;
      box-shadow:
        0px 3px 1px -2px $shadow-colour1,
        0px 2px 2px 0px $shadow-colour2,
        0px 1px 5px 0px $shadow-colour3;

      &::after {
        content: '';
        position: absolute;
        width: 100%;
        top: 0;
        pointer-events: none;
        bottom: 0;
      }
    }
    &.in-scope:last-child {
      margin-bottom: 15px;
    }
  }
}

.error-triangle-icon {
  color: $assortment-negative-action-colour;
  font-size: 2rem !important;
  cursor: pointer;
}

.main-panel {
  // min-height: 100%;
  // height: 100%;
  height: $tab-panel-height;
  overflow-y: auto;
  overflow-x: hidden;
}
</style>
